import { useState, useEffect, useCallback } from 'react';
import { statisticsApi } from '../services/api';
import { DashboardStatModel, StatisticsComparisonApiResponse } from '../types/statistics';

interface UseStatisticsResult {
  dailyData: DashboardStatModel[];
  weeklyData: DashboardStatModel[];
  dailyDataPrevYear: DashboardStatModel[];
  weeklyDataPrevYear: DashboardStatModel[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useStatistics = (days: number = 7): UseStatisticsResult => {
  const [dailyData, setDailyData] = useState<DashboardStatModel[]>([]);
  const [weeklyData, setWeeklyData] = useState<DashboardStatModel[]>([]);
  const [dailyDataPrevYear, setDailyDataPrevYear] = useState<DashboardStatModel[]>([]);
  const [weeklyDataPrevYear, setWeeklyDataPrevYear] = useState<DashboardStatModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch current data and weekly data for previous year
      const [dailyResponse, weeklyResponse, weeklyResponsePrevYear] = await Promise.all([
        statisticsApi.getDailyStats({ days }),
        statisticsApi.getWeeklyStats({ days, yearsOffset: 0 }),
        statisticsApi.getWeeklyStats({ days, yearsOffset: -1 })
      ]);


      if (dailyResponse.success && dailyResponse.data) {
        if (dailyResponse.data.data && Array.isArray(dailyResponse.data.data)) {
          setDailyData(dailyResponse.data.data);
        } else {
          setDailyData([]);
        }
      } else {
        throw new Error(dailyResponse.message || 'Errore nel recupero dati giornalieri');
      }

      if (weeklyResponse.success && weeklyResponse.data) {
        if (weeklyResponse.data.data && Array.isArray(weeklyResponse.data.data)) {
          setWeeklyData(weeklyResponse.data.data);
        } else {
          setWeeklyData([]);
        }
      } else {
        throw new Error(weeklyResponse.message || 'Errore nel recupero dati settimanali');
      }

      if (weeklyResponsePrevYear.success && weeklyResponsePrevYear.data) {
        if (weeklyResponsePrevYear.data.data && Array.isArray(weeklyResponsePrevYear.data.data)) {
          setWeeklyDataPrevYear(weeklyResponsePrevYear.data.data);
        } else {
          setWeeklyDataPrevYear([]);
        }
      } else {
        console.warn('Errore nel recupero dati settimanali anno precedente:', weeklyResponsePrevYear.message);
        setWeeklyDataPrevYear([]);
      }

      // Fetch comparison data (previous year)
      const comparisonData = await statisticsApi.getStatsWithComparison({ 
        days, 
        includeComparison: true 
      });

      if (comparisonData && comparisonData.success && comparisonData.data) {
        setDailyDataPrevYear(comparisonData.data.previousYear);
        // weeklyDataPrevYear is now set above from separate API call
      } else {
        setDailyDataPrevYear([]);
        // weeklyDataPrevYear is handled above
      }

    } catch (err: any) {
      console.error('Errore nel recupero delle statistiche:', err);
      setError(err.message || 'Errore sconosciuto nel recupero delle statistiche');
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return {
    dailyData,
    weeklyData,
    dailyDataPrevYear,
    weeklyDataPrevYear,
    isLoading,
    error,
    refetch: fetchStatistics
  };
};